import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { MeResponse } from '../types/auth.types';

/** Body of `PUT /api/auth/me`. Mirrors `UserMeDTO`. */
export interface UpdateMeRequest {
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  email?: string | null;
  avatar?: string | null;
}

/** Body of `PUT /api/auth/me/change-password`. Mirrors `UserMePasswordDTO`. */
export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
  newPasswordRepeat: string;
}

/** Body of `POST /api/auth/me/generate-change-email-otp`. */
export interface RequestEmailOtpRequest {
  email: string;
}

/** Body of `POST /api/auth/me/generate-change-phone-otp`. */
export interface RequestPhoneOtpRequest {
  phone: string;
}

/** Body of `PATCH /api/auth/me/update-email`. Mirrors `OtpUpdateEmailOrPhoneDto`. */
export interface UpdateEmailRequest {
  email: string;
  otp: string;
}

/** Body of `PATCH /api/auth/me/update-phone`. */
export interface UpdatePhoneRequest {
  phone: string;
  otp: string;
}

/**
 * Self-service endpoints for the current user (admin profile page, etc.).
 * Wraps the `auth-service` `/me`-prefixed routes.
 */
@Injectable({ providedIn: 'root' })
export class MeService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/auth/api/auth`;

  /** Re-fetches the current user profile from the back. */
  get(): Observable<MeResponse> {
    return this.http.get<MeResponse>(`${this.base}/me`);
  }

  updateProfile(payload: UpdateMeRequest): Observable<MeResponse> {
    return this.http.put<MeResponse>(`${this.base}/me`, payload);
  }

  changePassword(payload: ChangePasswordRequest): Observable<MeResponse> {
    return this.http.put<MeResponse>(`${this.base}/me/change-password`, payload);
  }

  requestEmailOtp(payload: RequestEmailOtpRequest): Observable<unknown> {
    return this.http.post(`${this.base}/me/generate-change-email-otp`, payload);
  }

  updateEmail(payload: UpdateEmailRequest): Observable<MeResponse> {
    return this.http.patch<MeResponse>(`${this.base}/me/update-email`, payload);
  }

  requestPhoneOtp(payload: RequestPhoneOtpRequest): Observable<unknown> {
    return this.http.post(`${this.base}/me/generate-change-phone-otp`, payload);
  }

  updatePhone(payload: UpdatePhoneRequest): Observable<MeResponse> {
    return this.http.patch<MeResponse>(`${this.base}/me/update-phone`, payload);
  }
}
